import { createContext, useCallback, useEffect, useState } from 'react';
import { api, GetToken, RemoveToken, SetToken } from '../services/api';
import authService, { ClearAccessExpiresAt, SetAccessExpiresAt } from '../services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setTokenState] = useState(GetToken());
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);

    const loadUser = useCallback(async () => {
        const storedToken = GetToken();
        if (!storedToken) {
            setLoading(false);
            setSessionExpired(false);
            return;
        }

        try {
            const userData = await api.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
            setSessionExpired(false);
        } catch (_error) {
            setSessionExpired(true);
            RemoveToken();
            setTokenState(null);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    useEffect(() => {
        const onTokenExpired = () => {
            RemoveToken();
            ClearAccessExpiresAt();
            setTokenState(null);
            setUser(null);
            setIsAuthenticated(false);
            setSessionExpired(true);
        };

        authService.onTokenExpired(onTokenExpired);
        return () => authService.removeTokenExpiredCallback(onTokenExpired);
    }, []);

    const login = async (email, password) => {
        const response = await api.login(email, password);
        SetToken(response.access_token);
        // Salva quando o access expira pra api.js poder fazer refresh
        // proativo (antes do request, evita roundtrip 401).
        const expiresIn = Number(response?.expires_in);
        if (Number.isFinite(expiresIn) && expiresIn > 0) {
            SetAccessExpiresAt(Date.now() + expiresIn * 1000);
        } else {
            ClearAccessExpiresAt();
        }
        setTokenState(response.access_token);
        setUser(response.user);
        setIsAuthenticated(true);
        setSessionExpired(false);
        return response;
    };

    const logout = async () => {
        try {
            await api.logout();
        } catch (_error) {
            // Silently handle logout errors
        } finally {
            RemoveToken();
            ClearAccessExpiresAt();
            setTokenState(null);
            setUser(null);
            setIsAuthenticated(false);
            setSessionExpired(false);
        }
    };

    const acknowledgeSessionExpired = () => {
        setSessionExpired(false);
    };

    const checkPermission = (permission) => {
        if (!permission) {
            return true;
        }

        const permissions = Array.isArray(user?.permissions) ? user.permissions : [];

        if (
            permissions.includes(permission) ||
            permissions.includes('admin') ||
            permissions.includes('developer') ||
            permissions.includes('*') ||
            permissions.includes('all:*')
        ) {
            return true;
        }

        // Umbrella do Supervisor: `wms:manage` cobre todas as `wms:manage:*`
        // (espelha o HasPermission do backend — front e back concordam).
        if (permission.startsWith('wms:manage:') && permissions.includes('wms:manage')) {
            return true;
        }

        if (permission === 'customers:read' && permissions.includes('orders:read')) {
            return true;
        }

        if (permission === 'purchasing:create' && permissions.includes('purchasing:write')) {
            return true;
        }

        if (permission === 'purchasing:update' && permissions.includes('purchasing:write')) {
            return true;
        }

        if (
            permission === 'purchasing:read' &&
            (permissions.includes('purchasing:write') ||
                permissions.includes('purchasing:create') ||
                permissions.includes('purchasing:update'))
        ) {
            return true;
        }

        if (
            permission === 'purchasing:write' &&
            (permissions.includes('purchasing:create') || permissions.includes('purchasing:update'))
        ) {
            return true;
        }

        return false;
    };

    const value = {
        user,
        token,
        isAuthenticated,
        loading,
        sessionExpired,
        login,
        logout,
        checkPermission,
        acknowledgeSessionExpired,
        refreshUser: loadUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
